// filepath: /Users/franconogarin/localcode/franco_react_playground/src/App.tsx
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate } from 'react-router-dom';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable/base';
import { registerAllModules } from 'handsontable/registry';
import FireSVG from './assets/fire.svg';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import './App.scss';
import EasyMap from './easymap/EasyMap';
import LoginPage from './LoginPage';
import LogoutPage from './LogoutPage';
import PrivateRoute from './PrivateRoute';
import { FusionAuthProvider, FusionAuthProviderConfig, RequireAuth, Unauthenticated, useFusionAuth } from '@fusionauth/react-sdk';
import { PermissionProvider } from './context/PermissionContext';
import { MessageProvider } from './components/common/AppMessages';
import { ConfirmProvider } from './components/common/AppConfirm';

import UserInfoInspector from './components/userComponents/UserInfoInspector';
import OAuthCallback from './components/userComponents/OAuthCallback';
import { getEnvironmentTitle } from './services/configService';
//import WeatherDataExplorer from './components/explorers/WeatherDataExplorer';

// Register Handsontable's modules
registerAllModules();

// Note: Mapbox token is set inside App component after config is loaded
// Do NOT call getConfig() or getMapboxToken() at module level - config isn't loaded yet

interface FireData {
  estimatedSize: any;
  number: string;
  latitudeDD: number;
  longitudeDD: number;
  fireLevel: string;
  wildfireStatus: string;

}


// Custom 404 Page
const NotFound = () => (
  <div>
    <h2>404 - Page Not Found</h2>
    <p>The page you are looking for does not exist.</p>
    <a href="/map">Go Home</a>
  </div>
);

// const NotFoundOrNotLoggedIn = () => (
//   <div>
//     <h2>Unauthorized</h2>
//     <p>The page you are looking for does not exist or might be unavailable becuase you are not logged in.</p>
//     <a href="/login">Login</a>
//   </div>
// );
const NotFoundOrNotLoggedIn = () => { return <Navigate to="/login" replace /> }


const POST_LOGIN_URL_KEY = 'em3:postLoginUrl';

const INIT_REFRESH_TIMEOUT_MS = 3000;

const App: React.FC = () => {
  const { isLoggedIn, isFetchingUserInfo, refreshToken } = useFusionAuth() as {
    isLoggedIn: boolean;
    isFetchingUserInfo: boolean;
    refreshToken: () => Promise<Response | undefined>;
  };

  // Auth init. Two states:
  // - cookieValid: at_exp cookie says we should be logged in; we just need
  //   to wait for the SDK's React isLoggedIn to catch up before rendering.
  // - refreshDone: refresh attempt resolved (success/failure/timeout); we
  //   know the genuine auth state and can render whatever the SDK shows.
  // initialized = cookieValid && isLoggedIn  ||  refreshDone.
  const [cookieValid, setCookieValid] = useState(false);
  const [refreshDone, setRefreshDone] = useState(false);

  useEffect(() => {
    const atExpRaw = document.cookie
      .split('; ')
      .find((c) => c.startsWith('app.at_exp='))?.split('=')[1];
    const atExpMs = atExpRaw ? parseInt(atExpRaw, 10) * 1000 : 0;
    const atExpValid = atExpMs > Date.now();
    console.log('[Auth init]', {
      atExpValid,
      atExpInSeconds: atExpMs ? Math.round((atExpMs - Date.now()) / 1000) : null,
      isLoggedInAtMount: isLoggedIn,
    });

    if (atExpValid) {
      setCookieValid(true);
      window.sessionStorage.removeItem('em3:reloadedAfterInitRefresh');
      // Backup: if the SDK never flips isLoggedIn for some reason, fall
      // through after 2s so we don't block forever.
      const sdkFallback = window.setTimeout(() => {
        console.warn('[Auth init] SDK isLoggedIn never flipped — falling through');
        setRefreshDone(true);
      }, 2000);
      return () => window.clearTimeout(sdkFallback);
    }

    let cancelled = false;
    let willReload = false;
    const timer = window.setTimeout(() => {
      if (cancelled || willReload) return;
      console.warn('[Auth init] refresh timed out — falling through to SDK state');
      setRefreshDone(true);
    }, INIT_REFRESH_TIMEOUT_MS);

    refreshToken()
      .then((res) => {
        if (cancelled) return;
        const ok = res?.ok ?? false;
        console.log('[Auth init] refresh result', { ok, status: res?.status });
        if (ok && !window.sessionStorage.getItem('em3:reloadedAfterInitRefresh')) {
          willReload = true;
          window.sessionStorage.setItem('em3:reloadedAfterInitRefresh', '1');
          console.log('[Auth init] reloading to let SDK pick up fresh cookies');
          window.location.reload();
          return;
        }
        window.sessionStorage.removeItem('em3:reloadedAfterInitRefresh');
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[Auth init] refresh threw', err?.message ?? err);
      })
      .finally(() => {
        if (cancelled || willReload) return;
        window.clearTimeout(timer);
        setRefreshDone(true);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialized when either: SDK reports logged in (cookie path), or the
  // refresh attempt resolved (no-cookie path).
  const initialized = (cookieValid && isLoggedIn) || refreshDone;
  useEffect(() => {
    console.log('[Auth init] state', {
      cookieValid,
      refreshDone,
      isLoggedIn,
      isFetchingUserInfo,
      initialized,
    });
  }, [cookieValid, refreshDone, isLoggedIn, isFetchingUserInfo, initialized]);

  useEffect(() => {
    document.title = "EasyMap3 - " + getEnvironmentTitle();
  }, []);

  // Refresh on tab return so the SDK timer doesn't drift while backgrounded.
  useEffect(() => {
    if (!initialized) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshToken();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [initialized, refreshToken]);

  // Restore the deep-link captured pre-mount in main.tsx, once the user
  // is authenticated. Capture happens before React mounts so the URL
  // isn't already wiped by <Navigate to="/login">. Defensively reject any
  // stashed value whose path is not /map — guards against stale stashes
  // (e.g. a previously-captured /app/front-channel-logout URL) from
  // earlier versions of the capture logic.
  useEffect(() => {
    if (isFetchingUserInfo || !isLoggedIn) return;
    const stashed = sessionStorage.getItem(POST_LOGIN_URL_KEY);
    if (stashed) {
      sessionStorage.removeItem(POST_LOGIN_URL_KEY);
      try {
        const stashedUrl = new URL(stashed);
        if (stashedUrl.pathname === '/map' && stashed !== window.location.href) {
          window.location.replace(stashed);
        }
      } catch { /* invalid URL — drop it */ }
    }
  }, [isLoggedIn, isFetchingUserInfo]);

  if (!initialized) {
    return null;
  }

  return (

    <>
      <RequireAuth>
        <PermissionProvider>
          <MessageProvider>
          <ConfirmProvider>
          <Router>
            <nav>
              {/* <Link to="/map">Map</Link> |
              <Link to="/admin">Admin</Link>  */}

            </nav>

            <Routes>
              {/* OAuth callback route - handles FusionAuth redirects (production only) */}
              <Route path="/callback" element={<OAuthCallback />} />

              {/* Redirect root to map by default */}
              <Route path="/" element={<Navigate to="/map" />} />

              {/* Your main map component */}
              <Route path="/map" element={<EasyMap />} />

              {/* Weather Data Explorer */}
              {/* <Route path="/weather-data-explorer" element={<WeatherDataExplorer />} /> */}

              {/* Other routes */}
              <Route path="/logout" element={<LogoutPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/userinfo" element={<UserInfoInspector />} />

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
          </ConfirmProvider>
          </MessageProvider>
        </PermissionProvider>
      </RequireAuth>

      <Unauthenticated>
        <Router>
          <nav>
            <ul>




              <li>
                <Link to="https://github.com/intellifire/" target="_blank" rel="noopener noreferrer">
                  Github
                </Link>
              </li>
            </ul>
          </nav>
          <Routes>



            <Route path="/login" element={<LoginPage />} />
            {/* Catch-All Route for 404 Pages */}
            <Route path="*" element={<NotFoundOrNotLoggedIn />} />
          </Routes>
        </Router>
      </Unauthenticated>
    </>



  );
};

export default App;
